import React, {useEffect, useState} from 'react';
import {C, btn} from './theme';

// Lighting effects on a block's lights lane. The engine owns the effect library (GET /engine/effects);
// a cue is {lane: 'lights', id, at, effect, params, beats?}. Beds hold the room until the next bed for
// their target; hits sit over whatever's underneath for `beats`.
let catalogCache = null;
export const useEffectCatalog = () => {
    const [catalog, setCatalog] = useState(catalogCache);
    useEffect(() => {
        if (catalogCache) return;
        fetch('/engine/effects').then(r => r.json()).then(c => { catalogCache = c; setCatalog(c); }).catch(() => {});
    }, []);
    return catalog;
};

const CSS_COLORS = {white: '#ffffff', red: '#ff2a2a', amber: '#ff8c00', yellow: '#ffdc00', green: '#20e04a', cyan: '#00e5ff',
    blue: '#2a5cff', purple: '#a020ff', magenta: '#ff20d0', black: '#000000'};
export const cssColor = (c) => CSS_COLORS[c] || c || '#fff';

const defaultsOf = (fx) => Object.fromEntries(fx.params.map(p => [p.name, p.default]));
export const newCue = (fx, at) => ({lane: 'lights', id: `q${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    at, effect: fx.id, params: defaultsOf(fx), ...(fx.kind === 'hit' ? {beats: fx.defaultBeats} : {})});

// How a cue looks on the lane: a swatch of its color(s).
export const cueSwatch = (cue, catalog) => {
    const p = cue.params || {};
    if (cue.effect === 'rainbow') return 'linear-gradient(90deg, #f33, #fd0, #3e5, #0ef, #36f, #c3f)';
    if (cue.effect === 'blackout' || cue.effect === 'hit') return 'repeating-linear-gradient(135deg, #000 0 4px, #1c1c1c 4px 8px)';
    if (cue.effect === 'chase') {
        const cols = (catalog && catalog.palettes[p.palette || 'rgb']) || ['red', 'green', 'blue'];
        return `linear-gradient(90deg, ${cols.map(c => cssColor(c)).join(', ')})`;
    }
    if (cue.effect === 'strobe') return `repeating-linear-gradient(90deg, ${cssColor(p.color || 'white')} 0 3px, #111 3px 7px)`;
    if (cue.effect === 'breathe') return `linear-gradient(90deg, ${cssColor(p.color || 'blue')}33, ${cssColor(p.color || 'blue')}, ${cssColor(p.color || 'blue')}33)`;
    if (cue.effect === 'wash') return `linear-gradient(90deg, transparent, ${cssColor(p.color || 'purple')} 35%)`;
    return cssColor(p.color || 'white');
};

const fmtBeats = (b) => b >= 1 ? `${b} beat${b === 1 ? '' : 's'}` : `1/${Math.round(1 / b)}`;

// Each cue's extent in block seconds: hits by their length, beds until the next bed that replaces them.
export const cueSpans = (cues, catalog, bpmAt, dur) => {
    const kinds = Object.fromEntries(((catalog && catalog.effects) || []).map(e => [e.id, e]));
    const lights = cues.filter(c => c.lane === 'lights' && c.effect).sort((a, b) => a.at - b.at);
    return lights.map(c => {
        const fx = kinds[c.effect], kind = fx ? fx.kind : 'bed', target = (c.params && c.params.target) || 'all';
        const beats = c.beats ?? (fx && fx.defaultBeats);
        let end;
        if (beats) end = c.at + beats * 60 / bpmAt(c.at);
        else {
            // An all-room bed only gives way to the next all-room bed; a group bed to its group's next, or an all-room one.
            const next = lights.find(o => {
                const t = (o.params && o.params.target) || 'all';
                return o.at > c.at && (kinds[o.effect] || {}).kind !== 'hit' && (t === 'all' || t === target);
            });
            end = next ? next.at : dur;
        }
        return {cue: c, kind, target, label: fx ? fx.label : c.effect, start: c.at, end: Math.min(dur, Math.max(end, c.at + 0.05))};
    });
};

export const EffectShelf = ({catalog}) => <div style={{display: 'flex', flexDirection: 'column', minHeight: 0}}>
    {!catalog && <div style={{color: C.dim, fontSize: 12, padding: 6}}>Connecting to the engine…</div>}
    {catalog && ['bed', 'hit'].map(kind => <div key={kind} style={{marginBottom: 6}}>
        <div style={{fontSize: 10, color: C.dim, letterSpacing: 0.8, textTransform: 'uppercase', padding: '2px 2px 4px'}}>{kind === 'bed' ? 'Looks · hold until the next one' : 'Hits · over the look'}</div>
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5}}>
            {catalog.effects.filter(e => e.kind === kind).map(fx => <div key={fx.id} draggable
                onDragStart={e => { e.dataTransfer.setData('application/x-effect', fx.id); e.dataTransfer.effectAllowed = 'copy'; }}
                title={`${fx.blurb} — drag onto a block's lights lane`}
                style={{padding: '5px 7px', borderRadius: 7, background: '#1f232b', border: `1px solid ${C.line}`, cursor: 'grab', display: 'flex', alignItems: 'center', gap: 6}}>
                <span style={{width: 12, height: 12, borderRadius: 3, flexShrink: 0, background: cueSwatch({effect: fx.id, params: defaultsOf(fx)}, catalog), border: '1px solid #ffffff33'}}/>
                <span style={{fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{fx.label}</span>
            </div>)}
        </div>
    </div>)}
</div>;

const Seg = ({options, value, onChange, render = String}) => <span style={{display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: `1px solid ${C.line}`}}>
    {options.map(o => <button key={String(o)} onClick={() => onChange(o)} style={{border: 'none', padding: '2px 7px', fontSize: 11, cursor: 'pointer',
        background: o === value ? C.accent : '#16191f', color: o === value ? '#000' : C.text}}>{render(o)}</button>)}
</span>;

// The selected cue's settings, in one row.
export const CueInspector = ({cue, catalog, onChange, onDelete, onAudition, onClose}) => {
    const fx = catalog && catalog.effects.find(e => e.id === cue.effect);
    if (!fx) return null;
    const p = {...defaultsOf(fx), ...(cue.params || {})};
    const set = (k, v) => onChange({...cue, params: {...p, [k]: v}});
    return <div style={{display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontSize: 11, color: C.dim, padding: '6px 14px', margin: '0 14px 6px',
        borderRadius: 8, background: '#16191f', border: `1px solid ${C.line}`}}>
        <span style={{width: 14, height: 14, borderRadius: 3, background: cueSwatch(cue, catalog), border: '1px solid #ffffff44'}}/>
        <b style={{color: C.text, fontSize: 12}}>{fx.label}</b>
        <span>at {cue.at.toFixed(2)}s</span>
        {fx.params.map(par => <span key={par.name} style={{display: 'inline-flex', alignItems: 'center', gap: 5}}>
            {par.label || par.name}
            {par.type === 'color'
                ? <span style={{display: 'inline-flex', gap: 3}}>{par.options.map(o => <button key={o} title={o} onClick={() => set(par.name, o)} style={{width: 15, height: 15, padding: 0, borderRadius: 8, cursor: 'pointer',
                    background: cssColor(o), border: p[par.name] === o ? '2px solid #fff' : '1px solid #0008', boxShadow: p[par.name] === o ? `0 0 6px ${cssColor(o)}` : 'none'}}/>)}</span>
                : <Seg options={par.options} value={p[par.name]} onChange={v => set(par.name, v)} render={par.type === 'beats' ? fmtBeats : String}/>}
        </span>)}
        <span style={{display: 'inline-flex', alignItems: 'center', gap: 5}}>target <Seg options={catalog.targets} value={p.target || 'all'} onChange={v => set('target', v)}/></span>
        <span style={{display: 'inline-flex', alignItems: 'center', gap: 5}}>length
            <Seg options={fx.kind === 'hit' ? [0.5, 1, 2, 4, 8, 16] : ['auto', 4, 8, 16, 32]} value={cue.beats ?? (fx.kind === 'hit' ? fx.defaultBeats : 'auto')}
                onChange={v => { const {beats, ...rest} = cue; onChange(v === 'auto' ? rest : {...rest, beats: v}); }}
                render={v => v === 'auto' ? 'until next' : fmtBeats(v)}/></span>
        <span style={{flex: 1}}/>
        <button onClick={onAudition} title="Play it on the rig now" style={{...btn, padding: '2px 9px'}}>▶ try</button>
        <button onClick={onDelete} title="Delete (⌫)" style={{...btn, padding: '2px 9px'}}>🗑</button>
        <button onClick={onClose} style={{...btn, padding: '2px 7px'}}>✕</button>
    </div>;
};
